import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

type Mode = 'login' | 'register' | 'forgot';

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy, OnChanges {
  @Input() startWith: Mode = 'login';
  @Input() showForgot = true;

  activeForm: Mode = 'login';
  toastMessage = '';
  returnUrl = '/account';

  formModel: Record<string, any> = {};

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      const mode = (data?.['mode'] as Mode) || this.startWith;
      this.activeForm = mode === 'forgot' && !this.showForgot ? 'login' : mode;
    });

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.returnUrl = params.get('returnUrl') || '/account';

        const email = (params.get('email') || '').trim();
        const fName = (params.get('fName') || '').trim();
        const lName = (params.get('lName') || '').trim();
        const phone = (params.get('phone') || '').trim();

        this.formModel = {
          ...(email ? { email } : {}),
          ...(fName ? { firstName: fName } : {}),
          ...(lName ? { lastName: lName } : {}),
          ...(phone ? { phoneNumber: phone } : {}),
        };

        try {
          sessionStorage.setItem('returnUrl', this.returnUrl);
        } catch {}
      });

    const nav = this.router.currentNavigation();
    const toastFromNav = nav?.extras?.state?.['toast'];
    const toastFromHistory = history.state?.toast;

    this.toastMessage = toastFromNav || toastFromHistory || '';

    if (this.toastMessage) {
      setTimeout(() => {
        this.toastMessage = '';
      }, 4000);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startWith']?.currentValue) {
      const v = changes['startWith'].currentValue as Mode;
      this.activeForm = v === 'forgot' && !this.showForgot ? 'login' : v;
    }
  }

  setForm(type: Mode) {
    if (type === 'forgot' && !this.showForgot) return;

    const email = (this.formModel?.['email'] || '').trim();
    const firstName = (this.formModel?.['firstName'] || '').trim();
    const lastName = (this.formModel?.['lastName'] || '').trim();
    const phone = (this.formModel?.['phoneNumber'] || '').trim();

    const queryParams: Record<string, string> = {};
    if (this.returnUrl) queryParams['returnUrl'] = this.returnUrl;
    if (email) queryParams['email'] = email;
    if (firstName) queryParams['fName'] = firstName;
    if (lastName) queryParams['lName'] = lastName;
    if (phone) queryParams['phone'] = phone;

    // keep URL in sync too
    if (type === 'login') {
      this.router.navigate(['/login'], { queryParams });
    }

    if (type === 'register') {
      this.router.navigate(['/register'], { queryParams });
    }

    if (type === 'forgot') {
      this.router.navigate(['/forgot-password'], { queryParams });
    }
  }

  onFormSubmit(res: any) {
    if (!res || res.status !== 'success') return;

    if (this.activeForm === 'register' && res?.status === 'success') {
      this.router.navigate(['/register-success'], {
        state: { message: res.message, userId: res.userId },
      });
    }
  }

  dismissToast(): void {
    this.toastMessage = '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
